import React from 'react';
import { ArrowRight, Play, Pause, FileText, CheckCircle2, Lock, Sparkles, UserPlus } from 'lucide-react';
import { TranscriptSegment, NoteBlock } from '../types';
import { useLanguage } from '../i18nContext';

interface PublicShareViewProps {
  onSignUp: () => void;
  onLogin: () => void;
}

// --- Mock Data (Read Only) ---
const segments: TranscriptSegment[] = [
  { id: 't1', speaker: 'Sarah', timestamp: '00:15', text: 'Okay, lets kick off the Q3 retrospective. I want to specifically focus on the marketing budget efficiency.' },
  { id: 't2', speaker: 'David', timestamp: '00:22', text: 'Agreed. Looking at the data, our paid ads CPA increased by 15% in August.' },
  { id: 't3', speaker: 'Sarah', timestamp: '00:45', text: 'Exactly. That is why I proposed shifting $50k to the influencer pilot program.' },
  { id: 't4', speaker: 'David', timestamp: '01:02', text: 'I ran the projections. If we get a 2% conversion rate on the pilot, we break even in week 3.' },
  { id: 't5', speaker: 'Elena', timestamp: '01:15', text: 'Is the creative team ready for that volume of content? Influencers need briefs.' },
];

const notes: NoteBlock[] = [
  { id: 'n1', level: 1, type: 'heading', content: 'Q3 Marketing Retro' },
  { id: 'n2', level: 2, type: 'bullet', content: 'Action: Reallocate $50k from Paid Ads -> Influencer Pilot.' },
  { id: 'n3', level: 2, type: 'bullet', content: 'Risk: Creative bandwidth for influencer briefs. Elena to check capacity.' },
  { id: 'n5', level: 3, type: 'paragraph', content: 'Overall sentiment was positive regarding the strategic pivot.' },
];

export default function PublicShareView({ onSignUp, onLogin }: PublicShareViewProps) {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      
      {/* 1. Public Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white font-bold text-lg">M</div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">S</div>
                <span className="text-sm text-gray-500">{t.public.sharedBy} <span className="font-semibold text-gray-900">Sarah Jenkins</span></span>
            </div>
        </div>
        <div className="flex items-center gap-4">
            <button onClick={onLogin} className="text-sm font-medium text-gray-600 hover:text-black">{t.public.login}</button>
            <button onClick={onSignUp} className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">{t.public.signup}</button>
        </div>
      </header>

      {/* 2. Read-Only Content Area */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-6 pb-32 grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Transcript Panel */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden h-[calc(100vh-140px)]">
             <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <FileText size={16} /> {t.workspace.transcript}
                </div>
                <div className="px-2 py-1 bg-gray-200 rounded text-[10px] font-bold text-gray-500 uppercase">{t.workspace.readOnly}</div>
             </div>
             <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {segments.map((seg) => (
                    <div key={seg.id} className="flex gap-4 opacity-80 hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">
                            {seg.speaker.charAt(0)}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-gray-900">{seg.speaker}</span>
                                <span className="text-xs text-gray-400">{seg.timestamp}</span>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{seg.text}</p>
                        </div>
                    </div>
                ))}
                {/* Fade out effect */}
                <div className="text-center py-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-gray-200 text-xs font-medium text-gray-500">
                        <Lock size={12} />
                        Sign up to view full transcript
                    </div>
                </div>
             </div>
          </div>

          {/* AI Notes Panel */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden h-[calc(100vh-140px)] relative">
             <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <Sparkles size={16} className="text-purple-500" /> {t.workspace.aiSummary}
                </div>
             </div>
             <div className="flex-1 overflow-y-auto p-8 space-y-4">
                {notes.map((note) => (
                    <div key={note.id}>
                        {note.type === 'heading' && <h2 className="text-xl font-bold text-gray-900 mb-2">{note.content}</h2>}
                        {note.type === 'bullet' && (
                            <div className="flex gap-3 items-start">
                                <div className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 bg-black"></div>
                                <p className="leading-7 text-gray-800 text-sm">{note.content}</p>
                            </div>
                        )}
                        {note.type === 'paragraph' && <p className="leading-7 text-gray-600 text-sm">{note.content}</p>}
                    </div>
                ))}
             </div>
             
             {/* Blur Overlay */}
             <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white via-white/90 to-transparent flex items-end justify-center pb-10">
                <div className="text-center space-y-2">
                    <p className="text-gray-900 font-semibold">{t.public.wantAction}</p>
                </div>
             </div>
          </div>

      </main>

      {/* 3. Sticky Registration Prompt (CTA) */}
      <div className="fixed bottom-6 left-6 right-6 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-3xl z-50">
          <div className="bg-gray-900 text-white p-1 rounded-2xl shadow-deep flex items-center justify-between pl-6 pr-2 py-2 md:py-1.5 animate-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-4">
                  <div className="hidden md:flex w-10 h-10 rounded-full bg-white/10 items-center justify-center">
                      <Sparkles size={18} className="text-yellow-400 fill-yellow-400" />
                  </div>
                  <div>
                      <div className="font-bold text-sm md:text-base">{t.public.join}</div>
                      <div className="text-xs text-gray-400">{t.public.joinDesc}</div>
                  </div>
              </div>
              
              {/* Premium Flat Button */}
              <button 
                onClick={onSignUp}
                className="relative group overflow-hidden bg-brand-500 hover:bg-brand-600 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-[0_4px_14px_0_rgba(132,204,22,0.39)] hover:shadow-[0_6px_20px_rgba(132,204,22,0.23)] transition-all active:scale-95 flex items-center gap-2 shrink-0"
              >
                 <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                 <span className="relative z-10 flex items-center gap-2">
                    {t.public.signupFree}
                    <ArrowRight size={16} />
                 </span>
              </button>
          </div>
      </div>

    </div>
  );
}